import React, { useState } from 'react';
import PageTitle from '../../components/PageTitle';
import {
  Box, Button, Card, CardContent, Chip, Divider, Grid, Tooltip, Typography,
} from '@mui/material';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import { useForm } from 'react-hook-form';
import { MerchantFilterCached } from "../../common/ProshipFormComponents";
import { FormProvider, RHFDateRangePickerField } from '../../common/hook-form';
import MUIModal from "../../components/MuiModal";
import Loader from '../../common/Loader';
import useGetData from './useGetData';
import { convertDate } from '../../helpers/UtilityHelper';
import { v4 as uuidv4 } from 'uuid';
import FilterListIcon from '@mui/icons-material/FilterList';
import ListAltIcon from '@mui/icons-material/ListAlt';
import CopyIcon from '../../common/CopyIcon';

// ─── Priority chip colour map ──────────────────────────────────────────────────
const PRIORITY_CONFIG = {
  1: { bg: '#e8f5e9', color: '#2e7d32', label: 'P1' },
  2: { bg: '#e3f2fd', color: '#1565c0', label: 'P2' },
  3: { bg: '#f3e5f5', color: '#6a1b9a', label: 'P3' },
  4: { bg: '#fff3e0', color: '#e65100', label: 'P4' },
  5: { bg: '#fff8e1', color: '#f57f17', label: 'P5' },
}
const DEFAULT_PRIORITY = { bg: '#ffebee', color: '#c62828', label: 'P6+' }

function getPriorityChip(priority) {
  const config = PRIORITY_CONFIG[priority] || DEFAULT_PRIORITY
  return (
    <Chip
      label={config.label + (priority > 5 ? ` (${priority})` : '')}
      size="small"
      sx={{
        fontWeight: 700, fontSize: '0.75rem', height: 24, minWidth: 40,
        backgroundColor: config.bg, color: config.color,
      }}
    />
  )
}

// ─── Main Component ────────────────────────────────────────────────────────────
const AllShipmentsDetails = () => {
  const [page, setPage] = useState(0);
  const [sizePerPage, setSizePerPage] = useState(20);
  const [filters, setFilters] = React.useState(null)

  const payload = { offset: (page) * sizePerPage, limit: sizePerPage, ...(filters || {}) }
  const { data, isLoading, isFetching } = useGetData(payload)
  const [open, setopen] = useState(false)
  const [cpData, setCPData] = useState()
  const resData = (data?.data || []).map((i) => ({ ...i, id: uuidv4() }))

  const columns = [
    {
      field: 'awbNumber',
      headerAlign: 'left',
      align: 'left',
      headerName: 'AWB Number',
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a', fontFamily: 'monospace' }}>
            {row.awbNumber || '—'}
          </Typography>
          <CopyIcon text={row.awbNumber} />
        </Box>
      ),
    },
    {
      field: 'merchantName',
      headerAlign: 'left',
      align: 'left',
      headerName: 'Merchant',
      flex: 1.2,
      sortable: false,
      renderCell: ({ row }) => (
        <Tooltip title={row.merchantName || ''} placement='top-start' arrow>
          <Typography sx={{
            fontSize: '0.82rem', fontWeight: 600, color: '#1a1a1a',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {row.merchantName || '—'}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: 'priorityBookedCount',
      headerAlign: 'center',
      align: 'center',
      headerName: 'Booked Priority',
      width: 140,
      sortable: false,
      renderCell: ({ row }) => getPriorityChip(row.priorityBookedCount),
    },
    {
      field: 'courierName',
      headerAlign: 'left',
      align: 'left',
      headerName: 'Final Courier Partner',
      flex: 1.2,
      sortable: false,
      renderCell: ({ row }) => {
        const name = row?.courierBooked?.courierName
        return (
          <Typography sx={{ fontSize: '0.82rem', fontWeight: name ? 600 : 400, color: name ? '#1a1a1a' : '#bdbdbd' }}>
            {name || '—'}
          </Typography>
        )
      },
    },
    {
      field: 'courierPartner',
      headerAlign: 'center',
      align: 'center',
      headerName: 'Priority Rule List',
      width: 150,
      sortable: false,
      renderCell: ({ row }) => {
        if (!row?.priorityList?.length) return (
          <Typography sx={{ fontSize: '0.8rem', color: '#bdbdbd' }}>—</Typography>
        )
        return (
          <Button
            size="small"
            variant="outlined"
            onClick={() => handleOpen(row.priorityList)}
            startIcon={<ListAltIcon sx={{ fontSize: 14 }} />}
            sx={{
              textTransform: 'none', fontWeight: 600, fontSize: '0.75rem', borderRadius: 2,
              borderColor: '#1565c0', color: '#1565c0',
              '&:hover': { backgroundColor: '#e3f2fd', borderColor: '#0d47a1' },
            }}
          >
            View
          </Button>
        )
      },
    },
  ];

  const methods = useForm({
    defaultValues: { merchant: '', fromDate: '', toDate: '' }
  });

  const { handleSubmit, reset } = methods;
  const onSubmit = (filters) => {
    const fromDate = convertDate(filters.fromDate) + " " + "00:00:00"
    const toDate = convertDate(filters.toDate) + " " + "23:59:59"

    const andfilter = [
      { field: "createdAt", operator: "gte", value: `Date('${fromDate}')` },
      { field: "createdAt", operator: "lte", value: `Date('${toDate}')` },
      { field: 'merchantId', operator: "eq", value: filters.merchant }
    ]

    setPage(0)
    setFilters({ andfilter })
  }

  const handleClose = () => setopen(false)
  const handleOpen = (data) => { setopen(true); setCPData(data) }

  return (
    <>
      <PageTitle>All Shipments</PageTitle>

      <Grid container spacing={2}>
        {/* ── Filter Card ── */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2.5, border: '1px solid #e8e8e8', boxShadow: 'none' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <FilterListIcon sx={{ fontSize: 18, color: '#1565c0' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
                  Filters
                </Typography>
              </Box>
              <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={3}>
                    <MerchantFilterCached />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <RHFDateRangePickerField name="created_date" label="Created Date" maxDate={new Date()} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        size="small"
                        sx={{
                          textTransform: 'none', fontWeight: 700, borderRadius: 2,
                          backgroundColor: '#1565c0', '&:hover': { backgroundColor: '#0d47a1' },
                        }}
                      >
                        Apply Filters
                      </Button>
                      <Button
                        type="button"
                        variant="outlined"
                        size="small"
                        onClick={() => { reset({ merchant: '', fromDate: '', toDate: '' }); setFilters(null); setPage(0) }}
                        sx={{
                          textTransform: 'none', fontWeight: 600, borderRadius: 2, color: '#757575',
                          borderColor: '#e0e0e0', '&:hover': { borderColor: '#bdbdbd', backgroundColor: '#fafafa' },
                        }}
                      >
                        Reset
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </FormProvider>
            </CardContent>
          </Card>
        </Grid>

        {/* ── Loading ── */}
        {(isLoading || isFetching) && (
          <Grid item xs={12}><Loader /></Grid>
        )}

        {/* ── Data Table ── */}
        <Grid item xs={12}>
          <Box sx={{ backgroundColor: '#fff', border: '1px solid #e8e8e8', borderRadius: 2.5, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.75, borderBottom: '1px solid #f0f0f0' }}>
              <Box>
                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#1a1a1a' }}>
                  Shipment Allocation Details
                </Typography>
                <Typography sx={{ color: '#9e9e9e', fontSize: '0.75rem', mt: 0.2 }}>
                  AWB-level view of courier priority allocation per shipment
                </Typography>
              </Box>
              {data?.count > 0 && (
                <Chip
                  label={`${data.count.toLocaleString()} shipment${data.count !== 1 ? 's' : ''}`}
                  size='small'
                  sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, backgroundColor: '#e3f2fd', color: '#1565c0' }}
                />
              )}
            </Box>
            <ProzoDataGrid
              columns={columns}
              autoHeight={true}
              rows={resData}
              rowHeight={48}
              sizePerPage={sizePerPage}
              setSizePerPage={setSizePerPage}
              setPage={setPage}
              page={page}
              pagination={true}
              hideFooterPagination={false}
              filterMode={"server"}
              rowCount={data?.count || 0}
            />
          </Box>
        </Grid>
      </Grid>

      {/* ── Priority List Modal ── */}
      <MUIModal
        open={open}
        handleClose={handleClose}
        title="Courier Partner Priority List"
        maxWidth="sm"
      >
        <Box sx={{ p: 1 }}>
          {cpData?.map((cp, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, py: 1.25, px: 1.5,
                borderRadius: 1.5,
                backgroundColor: i % 2 === 0 ? '#fafbfc' : '#fff',
                '&:not(:last-child)': { mb: 0.5 },
              }}
            >
              <Chip
                label={`#${i + 1}`}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: '0.72rem', height: 22, minWidth: 36,
                  backgroundColor: i === 0 ? '#e8f5e9' : i === 1 ? '#e3f2fd' : '#f5f5f5',
                  color: i === 0 ? '#2e7d32' : i === 1 ? '#1565c0' : '#757575',
                }}
              />
              <Typography sx={{ fontSize: '0.85rem', fontWeight: 500, color: '#1a1a1a' }}>
                {cp.replace(/_/g, " ")}
              </Typography>
            </Box>
          ))}
        </Box>
      </MUIModal>
    </>
  );
}

export default AllShipmentsDetails;
